import { useEffect, useState } from "react";
import { Target } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useLeadScoringSettings,
  useUpdateLeadScoringSettings,
} from "@/features/calls/hooks";

export default function LeadScoringTab() {
  const q = useLeadScoringSettings();
  const save = useUpdateLeadScoringSettings();

  const [enabled, setEnabled] = useState(true);
  const [criteria, setCriteria] = useState("");
  const [hot, setHot] = useState(80);
  const [warm, setWarm] = useState(50);
  const [error, setError] = useState("");

  useEffect(() => {
    if (q.data) {
      setEnabled(q.data.enabled);
      setCriteria(q.data.custom_criteria);
      setHot(q.data.hot_threshold);
      setWarm(q.data.warm_threshold);
    }
  }, [q.data]);

  if (q.isLoading) return <Skeleton className="h-64 w-full" />;

  async function onSave() {
    setError("");
    if (warm >= hot) {
      setError("Warm threshold must be lower than the hot threshold.");
      return;
    }
    try {
      await save.mutateAsync({
        enabled,
        custom_criteria: criteria,
        hot_threshold: hot,
        warm_threshold: warm,
      });
      toast.success("Lead scoring settings saved");
    } catch (e) {
      const err = e as {
        response?: { data?: Record<string, string | string[]> };
      };
      const first =
        err?.response?.data &&
        Object.values(err.response.data)[0];
      const msg = Array.isArray(first) ? first[0] : first;
      setError(msg || "Failed to save settings");
    }
  }

  async function onQuickToggle(next: boolean) {
    setEnabled(next);
    try {
      await save.mutateAsync({ enabled: next });
      toast.success(next ? "Lead scoring enabled" : "Lead scoring disabled");
    } catch {
      // Revert on error
      setEnabled(!next);
      toast.error("Failed to update setting");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="size-4 text-primary" />
          Lead Scoring
        </CardTitle>
        <CardDescription>
          AI rates each call as a sales lead (0–100). Configure thresholds and
          add business-specific scoring guidance.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <label className="flex cursor-pointer items-center justify-between rounded-md border bg-card px-3 py-2.5">
          <div>
            <div className="text-sm font-medium">Enable lead scoring</div>
            <div className="text-xs text-muted-foreground">
              Auto-score every completed call.
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onQuickToggle}
            disabled={save.isPending}
          />
        </label>

        <div className="space-y-2">
          <Label htmlFor="criteria">Scoring criteria (optional)</Label>
          <Textarea
            id="criteria"
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            placeholder={
              "Describe what makes a hot lead for your business.\n\n" +
              "Example: We're a dental clinic. Hot leads ask about insurance, " +
              "appointment times, or specific procedures (cleaning, whitening). " +
              "Cold leads are sales calls or wrong-number callers."
            }
            rows={6}
            maxLength={2000}
            disabled={!enabled}
          />
          <p className="text-xs text-muted-foreground">
            {criteria.length}/2000 — used to bias the AI's score for your
            industry.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="hot">Hot lead threshold</Label>
            <Input
              id="hot"
              type="number"
              min={1}
              max={100}
              value={hot}
              onChange={(e) => setHot(Number(e.target.value) || 0)}
              disabled={!enabled}
            />
            <p className="text-xs text-muted-foreground">
              Score ≥ this number → 🔥 Hot.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="warm">Warm lead threshold</Label>
            <Input
              id="warm"
              type="number"
              min={0}
              max={99}
              value={warm}
              onChange={(e) => setWarm(Number(e.target.value) || 0)}
              disabled={!enabled}
            />
            <p className="text-xs text-muted-foreground">
              Score ≥ this number (and below Hot) → Warm.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <div>
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
